import React from 'react';
import { useWeb } from '../context/WebContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { FaShieldAlt, FaAward, FaTools, FaCheckCircle, FaUserShield } from 'react-icons/fa';
import { motion } from 'framer-motion';

export default function About() {
  const { settings } = useWeb();
  useDocumentMeta(settings.meta.about.title, settings.meta.about.desc);

  // Stats Data
  const stats = [
    { icon: "🏆", title: "ISO 9001 Standards", desc: "We strictly enforce global quality guidelines in all mesh netting materials and hardware fittings." },
    { icon: "👷‍♂️", title: "Trained Personnel", desc: "Each technician undergoes safety drill trainings and high-rise equipment anchoring procedures." },
    { icon: "⏳", title: "5+ Years Experience", desc: "Serving clients since half a decade with thousands of satisfied apartments and properties secured." },
    { icon: "🛡️", title: "5-Year Net Warranty", desc: "Our products are back-tested against fading, knot loosening, and weather impact under long exposures." }
  ];

  return (
    <div className="bg-slate-50 py-16 sm:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Title Section */}
        <div className="text-center space-y-4 mb-20">
          <motion.span 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block text-xs uppercase bg-orange-50 text-orange-600 font-extrabold px-4 py-1.5 rounded-full border border-orange-200"
          >
            ★ About Our Company
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight"
          >
            Vengamamba <span className="text-orange-600">Enterprises</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-slate-500 max-w-2xl mx-auto text-sm sm:text-base font-medium"
          >
            Providing top-tier professional safety netting installation services with certified quality.
          </motion.p>
        </div>

        {/* Brand Story + Image Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-6"
          >
            <div className="space-y-2">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Our Heritage</span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 leading-tight">
                A Legacy of Quality & <span className="bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent">Structural Safety</span>
              </h2>
            </div>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              Established with the goal of securing high-rise living environments, Vengamamba Enterprises has grown into a leading name in balcony safety nets, pigeon bird prevention, and invisible grills installation. Our commitment lies in offering durable safety barriers that seamlessly blend with building exteriors.
            </p>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              We understand that safety cannot be compromised. That is why we source premium UV-stabilized copolymer nets and marine-grade stainless steel cables that resist wear and tear from harsh sunlight and heavy monsoons. Our technical execution staff are certified high-elevation rope access professionals trained for complex projects.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-orange-500 to-amber-500 rounded-3xl opacity-10 blur-xl"></div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
              <img 
                src="/about-company.png" 
                alt="Vengamamba Enterprises Safety Nets Installation" 
                className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-750 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-white flex items-center space-x-3">
                <div className="bg-orange-600 p-3 rounded-xl shadow-lg">
                  <FaShieldAlt className="text-xl text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-sm">Certified Site Inspections</h4>
                  <p className="text-xs text-slate-300">Free measurements and safety audits</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-24"
        >
          {stats.map((stat, idx) => (
            <motion.div 
              key={idx}
              whileHover={{ y: -6 }}
              className="bg-white p-6 rounded-2xl border border-slate-200/80 hover:border-orange-500/20 shadow-sm hover:shadow-md transition-all duration-300 space-y-4"
            >
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-2xl shadow-inner">
                {stat.icon}
              </div>
              <h3 className="font-bold text-slate-900 text-lg">{stat.title}</h3>
              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">{stat.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Mission and Vision Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-900 text-white rounded-3xl p-8 sm:p-10 space-y-4 relative overflow-hidden group shadow-lg"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/10 rounded-full blur-2xl transform translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-700"></div>
            <span className="text-xs font-bold text-orange-500 uppercase tracking-widest block">Our Target</span>
            <h3 className="text-2xl font-bold text-white">Our Mission</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              To deliver premium quality, durable, and cost-effective safety net solutions that protect lives and property, preventing accidents on high-rise balconies and keeping premises clean from pigeon droppings. We aim to offer hassle-free bookings and same-day professional installation.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-orange-600 to-orange-700 text-white rounded-3xl p-8 sm:p-10 space-y-4 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl transform translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-700"></div>
            <span className="text-xs font-bold text-slate-900 uppercase tracking-widest text-opacity-80 block">Our Future</span>
            <h3 className="text-2xl font-bold text-white">Our Vision</h3>
            <p className="text-slate-100 text-sm leading-relaxed">
              To be the most reliable and trusted brand for safety netting installations across major Indian metros, continually innovating in material science like invisible grills, bite-proof pet netting, and robust sports installations.
            </p>
          </motion.div>
        </div>

        {/* Quality Standards & Guidelines */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-white rounded-3xl border border-slate-200/80 p-8 sm:p-12 space-y-8 shadow-sm"
        >
          <div className="max-w-3xl">
            <span className="text-xs font-bold text-orange-600 uppercase tracking-wider block mb-2">Engineering Safety</span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">Our Strict Quality Standards</h3>
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed">
              We adhere to strict installation protocols to guarantee long-term safety.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pt-4 border-t border-slate-100">
            <div className="flex space-x-3.5">
              <FaCheckCircle className="text-orange-500 text-lg flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-slate-900 text-sm sm:text-base">Material Integrity</h4>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed mt-1">We never use cheap nylon materials that degrade rapidly under sunlight.</p>
              </div>
            </div>

            <div className="flex space-x-3.5">
              <FaCheckCircle className="text-orange-500 text-lg flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-slate-900 text-sm sm:text-base">Double Anchoring System</h4>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed mt-1">Every corner and spacing bracket is double-anchored using heavy-duty bolts.</p>
              </div>
            </div>

            <div className="flex space-x-3.5">
              <FaCheckCircle className="text-orange-500 text-lg flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-slate-900 text-sm sm:text-base">Aesthetic Compliance</h4>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed mt-1">Nets are placed tightly and parallel so they don't look messy from the outside.</p>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
